import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CTMapFiltrableProperties } from '@cloud-twin-v2/ct-map/models/ct-map-filtrable-properties';
import { ElementTypeCategoriesDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { LayerEventsService } from '@common-modules/wlm-map/components/layers-selector/services/layer-events.service';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { MapStep } from '@common-modules/wlm-map/models/map-step';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { MasterOperatorConnectItemsSettings } from '../../../../thin-client/src/app/master-operator/models/master-operator-connect-settings';
import { ScenarioItem } from '../scenarios-configuration/scenario-item';

const COMPONENT_SELECTOR = 'ct-top-bar-buttons';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './ct-top-bar-buttons.component.html',
  styleUrl: './ct-top-bar-buttons.component.scss',
  standalone: false,
})
@UntilDestroy()
export class CtTopBarButtonsComponent implements OnInit {
  @Input() connectSettings: MasterOperatorConnectItemsSettings;
  @Input() attributesMetadata: Map<string, ElementTypeCategoriesDto[]>;
  @Input() step: MapStep;

  protected readonly _destroyRef = inject(DestroyRef);

  private readonly _layersService: LayerEventsService = inject(LayerEventsService);
  layers: LayerItemV2[];
  isScenariosPopupVisible: boolean;
  isMapLegendPopupVisible: boolean;

  private _stepChanged$: Observable<MapStep>;
  public get stepChanged$(): Observable<MapStep> {
    return this._stepChanged$;
  }
  public set stepChanged$(value: Observable<MapStep>) {
    this._stepChanged$ = value;
    this._stepChangedSubscription?.unsubscribe();

    this._stepChangedSubscription = this._stepChanged$?.subscribe(
      (mapStep) => (this.step = mapStep),
    );
  }

  private _connectSettingsChanged$: Observable<MasterOperatorConnectItemsSettings>;
  public get connectSettingsChanged$(): Observable<MasterOperatorConnectItemsSettings> {
    return this._connectSettingsChanged$;
  }
  public set connectSettingsChanged$(value: Observable<MasterOperatorConnectItemsSettings>) {
    this._connectSettingsChanged$ = value;
    this._connectSettingsChangedSubscription?.unsubscribe();
    this._connectSettingsChangedSubscription = this._connectSettingsChanged$?.subscribe(
      (connectSettings) => {
        this.connectSettings = connectSettings;
        this.mapConnectSettingsToScenario();
      },
    );
  }

  private _attributesMetadataChanged$: Observable<Map<string, ElementTypeCategoriesDto[]>>;
  public get attributesMetadataChanged$(): Observable<Map<string, ElementTypeCategoriesDto[]>> {
    return this._attributesMetadataChanged$;
  }
  public set attributesMetadataChanged$(
    value: Observable<Map<string, ElementTypeCategoriesDto[]>>,
  ) {
    this._attributesMetadataChanged$ = value;
    this._attributesMetadataChangedSubscription?.unsubscribe();
    this._attributesMetadataChangedSubscription = this._attributesMetadataChanged$?.subscribe(
      (metaData) => (this.attributesMetadata = metaData),
    );
  }

  private _params: any;
  public get params(): any {
    return this._params;
  }
  public set params(v: any) {
    this._params = v;
    if (v) {
      this.setSettings(v);
    }
  }

  scenarios: ScenarioItem[];
  scenarioVisibilityChange$ = new ReplaySubject<ScenarioItem>(1);
  namespacesChange$ = new ReplaySubject<Partial<CTMapFiltrableProperties>>(1);

  private _stepChangedSubscription: Subscription;
  private _attributesMetadataChangedSubscription: Subscription;
  private _connectSettingsChangedSubscription: Subscription;

  ngOnInit(): void {
    this.mapConnectSettingsToScenario();
    this._layersService.allLayersVisibles$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((layers) => {
        this.layers = layers ? layers.filter((l) => l.layerDefinition?.isThematic === true) : [];
      });
  }

  private setSettings(params: any) {
    this.connectSettingsChanged$ = params.connectSettingsChanged$;
    this.attributesMetadataChanged$ = params.attributesMetadataChanged$;
    this.stepChanged$ = params.stepChanged$;
    this.scenarioVisibilityChange$ = params.scenarioVisibilityChange$;
    this.namespacesChange$ = params.namespacesChange$;
  }

  private mapConnectSettingsToScenario(): void {
    if (this.connectSettings && this.connectSettings.items) {
      this.scenarios = this.connectSettings.items.map(
        (item) => new ScenarioItem(item.scenarioId, item.scenarioName, true), //TODO: visibility = true temporarily
      );
    }
  }

  onScenarioVisibilityChange(scenario: ScenarioItem): void {
    this.scenarioVisibilityChange$.next(scenario);
  }

  onNamespacesChange(namespaces: Partial<CTMapFiltrableProperties>): void {
    this.namespacesChange$.next(namespaces);
  }

  checkScenarioPopupStatus(isPopupVisible: boolean) {
    this.isScenariosPopupVisible = isPopupVisible;
    if (isPopupVisible) {
      this.isMapLegendPopupVisible = false;
    }
  }

  checkMapLegendPopupStatus(isPopupVisible: boolean) {
    this.isMapLegendPopupVisible = isPopupVisible;
    if (isPopupVisible) {
      this.isScenariosPopupVisible = false;
    }
  }
}
